import React, { useEffect, useRef, useState } from 'react';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { applyPassing, Chip, FADE_AFTER, getPassing, layout, PassingDirection, prune, Station } from '../data/passing';

/** How often the live flow asks what passed; the server keeps one reading a few seconds for everyone. */
const EVERY = 4000;
/** On opening, only what passed this recently moves; older moments of the first reading are left out. */
const FIRST_LOOK = 45000;

/** The objects passing an environment's connector now, as chips, and when the last reading was. */
export const usePassing = (customer?: string, environment?: string) => {
    const axios = useAxiosSwagger();
    const [chips, setChips] = useState<Map<string, Chip>>(new Map());
    const [readAt, setReadAt] = useState<string>();
    const [error, setError] = useState(false);
    const since = useRef<string>();

    useEffect(() => {
        setChips(new Map());
        since.current = undefined;
        if (!axios || !customer || !environment) return undefined;
        let stopped = false;
        let controller: AbortController | undefined;

        const read = async () => {
            if (document.hidden) return;
            controller = new AbortController();
            try {
                const passing = await getPassing(axios, customer, environment, since.current, controller.signal);
                if (stopped) return;
                const first = since.current === undefined;
                const edge = Date.parse(passing.readAt) - FIRST_LOOK;
                const items = first ? passing.items.filter((i) => Date.parse(i.at) >= edge) : passing.items;
                since.current = passing.readAt;
                setReadAt(passing.readAt);
                setError(false);
                setChips((now) => prune(applyPassing(now, items), Date.now()));
            } catch {
                if (!stopped) setError(true);
            }
        };

        read();
        const timer = setInterval(read, EVERY);
        return () => {
            stopped = true;
            controller?.abort();
            clearInterval(timer);
        };
    }, [axios, customer, environment]);

    // Chips fade and go between readings too.
    useEffect(() => {
        const timer = setInterval(() => setChips((now) => prune(now, Date.now())), 1000);
        return () => clearInterval(timer);
    }, []);

    return { chips, readAt, error };
};

/**
 * The left edge of a station on a wire, as a share of the flow map: the same five columns as the map above it
 * (160px, 1fr, 240px, 1fr, 160px), so a chip at "connector" sits under the connector. To Exact runs left to right,
 * to Chargebee right to left.
 */
const LEFT: Record<PassingDirection, Record<Station, string>> = {
    ToExact: { from: '80px', connector: '50%', to: 'calc(100% - 80px)' },
    ToChargebee: { from: 'calc(100% - 80px)', connector: '50%', to: '80px' },
};

const ROW = 24;

const Lane = ({ chips, direction, label }: { chips: Map<string, Chip>; direction: PassingDirection; label: string }) => {
    const { placed, more } = layout(chips, direction);
    // A new chip is drawn first where it came from and moved to its station on the next frame, so it travels.
    const [arrived, setArrived] = useState<Set<string>>(new Set());
    const pending = placed.filter((p) => !arrived.has(p.chip.key)).map((p) => p.chip.key);

    useEffect(() => {
        if (pending.length === 0) return undefined;
        const frame = requestAnimationFrame(() =>
            requestAnimationFrame(() => setArrived((was) => new Set([...Array.from(was).filter((k) => chips.has(k)), ...pending])))
        );
        return () => cancelAnimationFrame(frame);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pending.join('|')]);

    const now = Date.now();
    return (
        <div className="lo-lane" style={{ height: ROW * 3 + 8 }} aria-label={label}>
            <span className="lo-name">{label}</span>
            {placed.map(({ chip, slot }) => {
                const station = arrived.has(chip.key) ? chip.station : 'from';
                const fading = chip.done && now - chip.lastAt > FADE_AFTER;
                return (
                    <span
                        key={chip.key}
                        className={`lo-chip ${chip.tone} ${fading ? 'fading' : ''}`}
                        style={{ left: LEFT[direction][station], top: 4 + slot * ROW }}
                        title={chip.title}
                    >
                        <b>{chip.label}</b>
                        <small>{chip.status}</small>
                    </span>
                );
            })}
            {(Object.keys(more) as Station[]).map((station) => (
                <span key={station} className="lo-more" style={{ left: LEFT[direction][station], top: 4 + 3 * ROW - 6 }}>
                    +{more[station]}
                </span>
            ))}
        </div>
    );
};

/** The objects on their way, under the flow map: one wire to Exact, one back to Chargebee. */
export const LiveObjects = ({ chips, error }: { chips: Map<string, Chip>; error?: boolean }) => (
    <div className="lo">
        <Lane chips={chips} direction="ToExact" label="Naar Exact" />
        <Lane chips={chips} direction="ToChargebee" label="Naar Chargebee" />
        {chips.size === 0 && <div className="lo-empty mut">{error ? 'Kon niet lezen wat voorbijkomt' : 'Nu niets onderweg'}</div>}
    </div>
);
